import { CommonModule } from '@angular/common';
import { Component, inject, model, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { DataProperty } from '@models/data-property.model';
import { FileModel } from '@models/file.model';
import { ModelOutputFile } from '@models/model-output-file.model';
import { VariableOutputModel } from '@models/variable-output-model';
import { ModelType } from '@shared/constants/model-type.enum';
import { DataPropertiesService } from 'src/app/services/data-properties.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-data-properties',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule,],
  templateUrl: './data-properties.component.html',
  styleUrl: './data-properties.component.scss'
})
export class DataPropertiesComponent {
  private dataPropertiesService: DataPropertiesService = inject(DataPropertiesService);

  loading: boolean = false;
  projectId: number;
  modelType: ModelType;
  propertiesData: DataProperty[] = [];
  parentLabel: string = '';

  constructor(private route: ActivatedRoute){

    this.route.queryParams.subscribe(params => {      
      this.projectId = params['projectId'];
      this.modelType = params['modelType'];
      this.parentLabel = params['parentLabel'];
      this.getData();
    });
    
  }

  getData(){
    if(this.projectId && this.modelType){
      this.loading = true;
      this.dataPropertiesService.getDataProperties(this.projectId, this.modelType).subscribe({
        next: (data: any) => {
          this.loading = false;
          this.propertiesData = data;
        },
        error: (err: any) => {
          this.loading = false;
          console.error('Error fetching data properties', err);
        }
      });
    }
  }

  onEgModelOutputFileClick(fileId: number){
    this.loading = true;
    this.dataPropertiesService.getEgModelOutputFile(fileId, this.modelType).subscribe({ 
      next: (data: ModelOutputFile) => {
        this.openNewWindowWithContent(data.fileData);
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error fetching file', err);
        this.loading = false;
      }
    });
  }

  onSourceDataFileClick(file: FileModel){
    this.loading = true;
    this.dataPropertiesService.getFile(file.attachmentId, this.modelType).subscribe({
      next: (data: FileModel) => {
        this.downloadFile(data);
        this.loading = false;
      },
      error: (err: any) => {
        this.loading = false;
      }
    });
  }

  openNewWindowWithContent(content: string){
    const cleanedContent = content.replace(/<link[^>]*>/gi, '');
    const newWindow = window.open('', '_blank');
    newWindow.document.write(cleanedContent);
    newWindow.document.close();
  }

  onVariablesDataFileClick(modelId: number){
    this.loading = true;

    this.dataPropertiesService.getVariablesData(modelId, this.modelType).subscribe({
      next: (data: VariableOutputModel[]) => {
        if(data.length > 0){
          this.sendToExcel(data);          
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error fetching variable output data', err);
        this.loading = false;
      } 
    })
  }

  downloadFile(fileModel: FileModel): void {
    const byteArray = Uint8Array.from(atob(fileModel.file.toString()), c => c.charCodeAt(0));
    const blob = new Blob([byteArray], { type: 'application/octet-stream' });
    const file = new File([blob], fileModel.fileName.split('/').pop(), { type: 'application/octet-stream' });

    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  sendToExcel(data: VariableOutputModel[]): void {    
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'variable output');
    XLSX.writeFile(wb, this.parentLabel + '.xlsx');
  }
}
